import { isNil } from 'lodash-es';
import type { ReactNode } from 'react';
import type { BuildingLevel, Category, GuideLine } from '@/graphql/types';
import { createContext, useContext } from 'react';
import i18n from './translations/i18n';
import { FALLBACK_TIMEZONE, getBrowserTimezone } from './utils/get-browser-timezone';

declare global {
    interface Window {
        hj: (event: string, data?: unknown) => void;
    }
}

interface ResellerData {
    isActive: boolean;
    topLogoUrl: string | null;
    companyName: string;
}

export interface DefaultVibrationSettings {
    guideLine: GuideLine;
    buildingLevel: BuildingLevel;
    category?: Category;
    dataSaveLevel: number;
    traceSaveLevel: number;
    flatLevel?: number;
    atopFlatLevel: number;
}

export class Profile {
    declare use24HourNotation: boolean;

    declare userId: number | null;

    declare languageCode: Language;

    declare distanceUnit: DistanceUnit;

    declare isProfileIncomplete: boolean;

    declare isAuthenticated: boolean;

    declare isReseller: boolean;

    declare isCustomer: boolean;

    declare canAccessReportBuilderProjectTemplates: boolean;

    declare canAccessLegacyPdfTemplates: boolean;

    declare resellerData: ResellerData;

    declare readyReportsCount: number;

    declare csrfToken: string | null;

    declare preferredTimezone: string;

    declare defaultVibrationSettings: DefaultVibrationSettings;

    constructor() {
        this.use24HourNotation = true;
        this.userId = null;
        this.languageCode = 'en';
        this.distanceUnit = 'mm';
        this.isProfileIncomplete = false;
        this.isAuthenticated = false;
        this.isReseller = false;
        this.isCustomer = false;
        this.canAccessReportBuilderProjectTemplates = false;
        this.canAccessLegacyPdfTemplates = false;
        this.resellerData = {
            isActive: false,
            topLogoUrl: null,
            companyName: '',
        };
        this.readyReportsCount = 0;
        this.csrfToken = null;
        this.preferredTimezone = FALLBACK_TIMEZONE;
    }

    load(options: ProfileOptions) {
        // If there's no preferred timezone yet, set the browser timezone.
        if (isNil(options?.preferredTimezone)) {
            options.preferredTimezone = getBrowserTimezone();
        }

        // Assign all the options coming from Django or Storybook to the active profile instance.
        Object.assign(this, options);
    }
}

const profileInstance = new Profile();

export function profileModule({ options }: { options: ProfileOptions }) {
    // eslint-disable-next-line no-void
    void i18n.changeLanguage(options.languageCode);

    profileInstance.load(options);
}

export interface ProfileOptions {
    languageCode?: Language;
    distanceUnit?: string;
    preferredTimezone?: string;
    use24HourNotation?: boolean;
    userId?: number | null;
    isAuthenticated?: boolean;
    isProfileIncomplete?: boolean;
    isReseller?: boolean;
    isCustomer?: boolean;
    canAccessReportBuilderProjectTemplates?: boolean;
    canAccessLegacyPdfTemplates?: boolean;
    resellerData?: ResellerData;
    readyReportsCount?: number;
    csrfToken?: string | null;
    defaultVibrationSettings: DefaultVibrationSettings;
}

export default profileInstance;

const ProfileContext = createContext<Profile | undefined>(undefined);

// distanceUnit is a plain mutable property — changing it does not trigger re-renders.
// The only supported flow is profile page → page refresh, so stale reads are not
// currently a problem. The long-term goal is to expose distanceUnit as an observable
// so that components re-render automatically when the unit changes, without a page refresh.
export function useProfile() {
    const context = useContext(ProfileContext);

    if (!context) {
        throw new Error('No profile context available.');
    }

    return context;
}

export function useOptionalProfile(): Profile | undefined {
    return useContext(ProfileContext);
}

export function ProfileProvider({ profile, children }: ProfileProviderProps) {
    return <ProfileContext.Provider value={profile}>{children}</ProfileContext.Provider>;
}

export interface ProfileProviderProps {
    profile: Profile;
    children: ReactNode;
}
